import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { register, login, loginWithGoogle, pinLogin } from "@/lib/userAuth";
import { Loader2, UserPlus, LogIn, Key, ShieldCheck } from "lucide-react";

export default function AccediRegistrati({ mode = "login" }) {
  const nav = useNavigate();
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pinMode, setPinMode] = useState(false);
  const [pinForm, setPinForm] = useState({ email: "", pin: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      if (tab === "register") {
        await register(form);
      } else {
        await login(form.email, form.password);
      }
      // Resume pending booking if any
      let pending = null;
      try { pending = JSON.parse(sessionStorage.getItem("labor_pending_booking") || "null"); } catch {}
      if (pending?.returnPath) {
        nav(pending.returnPath);
      } else {
        nav("/profilo");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Errore, riprova.");
    } finally { setLoading(false); }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await pinLogin(pinForm.email, pinForm.pin);
      let pending = null;
      try { pending = JSON.parse(sessionStorage.getItem("labor_pending_booking") || "null"); } catch {}
      if (pending?.returnPath) nav(pending.returnPath); else nav("/profilo");
    } catch (err) {
      setError(err.response?.data?.detail || "PIN o email non corretti");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <div className="grid gap-3 mb-8" data-testid="login-role-choice">
        <div className="p-4 bg-labor-olive text-white font-bold text-lg underline underline-offset-4 decoration-2 flex items-center gap-3" data-testid="login-assistito-heading">
          <LogIn className="w-5 h-5 flex-shrink-0" /> Accedi Assistito ad Area Riservata
        </div>
        <Link to="/admin" data-testid="login-operatore-link"
          className="p-4 bg-labor-ink text-white font-bold text-lg underline underline-offset-4 decoration-2 flex items-center gap-3 hover:opacity-90 transition-opacity">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" /> Accedi Operatore Labor
        </Link>
      </div>
      <div className="text-xs uppercase tracking-widest text-labor-muted">Area Assistiti · Labor AI</div>
      <h1 className="font-display text-4xl mt-2 mb-6">{pinMode ? "Accesso rapido con PIN" : tab === "register" ? "Crea il tuo account" : "Accedi al tuo account"}</h1>

      {pinMode ? (
        <>
          <form onSubmit={submitPin} className="paper-card p-6 space-y-4">
            <input
              data-testid="pin-email"
              required
              type="email"
              placeholder="Email"
              value={pinForm.email}
              onChange={(e) => setPinForm({...pinForm, email: e.target.value})}
              className="w-full px-3 py-3 border border-labor-border"
            />
            <input
              data-testid="pin-code"
              required
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="PIN (4-6 cifre)"
              value={pinForm.pin}
              onChange={(e) => setPinForm({...pinForm, pin: e.target.value.replace(/\D/g, "")})}
              className="w-full px-3 py-3 border border-labor-border text-center font-display text-3xl tracking-widest"
            />
            {error && <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200">{error}</div>}
            <button data-testid="pin-submit" disabled={loading} type="submit" className="w-full olive-btn py-3.5 font-semibold flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Accedi con PIN
            </button>
          </form>
          <button data-testid="pin-back" onClick={() => { setPinMode(false); setError(null); }} className="w-full mt-3 text-labor-muted hover:text-labor-ink text-sm underline">
            ← Torna al login con password
          </button>
        </>
      ) : (
      <>
      <div className="flex mb-6 border-b border-labor-border">
        <button data-testid="tab-login" onClick={() => setTab("login")} className={`flex-1 py-3 font-medium ${tab === "login" ? "border-b-2 border-labor-olive text-labor-olive" : "text-labor-muted"}`}>Accedi</button>
        <button data-testid="tab-register" onClick={() => setTab("register")} className={`flex-1 py-3 font-medium ${tab === "register" ? "border-b-2 border-labor-terracotta text-labor-terracotta" : "text-labor-muted"}`}>Registrati</button>
      </div>

      {/* PIN rapido link (solo su login) */}
      {tab === "login" && (
        <button
          data-testid="use-pin-btn"
          onClick={() => { setPinMode(true); setError(null); }}
          type="button"
          className="w-full flex items-center justify-center gap-2 border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white py-3 font-medium mb-3"
        >
          <Key className="w-4 h-4" /> Accedi con PIN rapido
        </button>
      )}

      {/* GOOGLE LOGIN */}
      <button
        data-testid="google-login-btn"
        onClick={loginWithGoogle}
        type="button"
        className="w-full flex items-center justify-center gap-3 border border-labor-border py-3 font-medium hover:bg-labor-bg mb-5 bg-white"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continua con Google
      </button>

      <div className="relative mb-5">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-labor-border"></div></div>
        <div className="relative flex justify-center text-xs uppercase tracking-widest"><span className="bg-labor-bg px-3 text-labor-muted">oppure</span></div>
      </div>

      <form onSubmit={submit} className="paper-card p-6 space-y-4">
        {tab === "register" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="reg-first-name" required placeholder="Nome" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="px-3 py-3 border border-labor-border" />
              <input data-testid="reg-last-name" required placeholder="Cognome" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="px-3 py-3 border border-labor-border" />
            </div>
            <input data-testid="reg-phone" required placeholder="Telefono (obbligatorio) *" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-3 py-3 border border-labor-border" />
          </>
        )}
        <input data-testid="auth-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-3 border border-labor-border" />
        <input data-testid="auth-password" required type="password" placeholder="Password (min. 6 caratteri)" minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full px-3 py-3 border border-labor-border" />

        {error && <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200">{error}</div>}

        {tab === "register" && (
          <div className="text-xs text-labor-muted bg-labor-skyBg p-3 border-l-4 border-labor-italianGreen">
            🎁 <strong>Bonus di benvenuto</strong>: 20 domande gratuite a Labor AI subito attive dopo la registrazione.
          </div>
        )}

        <button data-testid="auth-submit" disabled={loading} type="submit" className="w-full olive-btn py-3.5 font-semibold flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === "register" ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
          {tab === "register" ? "Registrati gratis" : "Accedi"}
        </button>

        <div className="text-xs text-center text-labor-muted">
          Registrandoti accetti la nostra <Link to="/privacy-policy" className="underline">Privacy Policy</Link>. I tuoi dati sono trattati nel rispetto del GDPR.
        </div>
      </form>
      </>
      )}
    </div>
  );
}
